import { Fragment, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppLogo } from '@/components/common/AppLogo';
import { AppWordmark } from '@/components/common/AppWordmark';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { useSidebar } from '@/components/ui/sidebar-context';
import { NAV_SECTIONS } from '@/config/navigation';
import { filterNavSections, useNavVisibilityContext } from './utils/nav.utils';
import { SidebarSearch } from './SidebarSearch';
import { CollapsibleNavGroup } from './CollapsibleNavGroup';
import { SidebarToggleButton } from './SidebarToggleButton';
import { VendorCredit } from '@/components/common/VendorCredit';
import { cn } from '@/lib/utils';
import {
  FOOTER_SEPARATOR_CLASS,
  SECTION_CLASS,
  SECTION_LABEL_CLASS,
  SECTION_SEPARATOR_CLASS,
  SIDEBAR_FOOTER_CLASS,
  VENDOR_CREDIT_CLASS,
} from './sidebar.constants';

const HEADER_CLASS =
  'relative flex-row items-center px-6 py-3.5 transition-[padding] duration-300 ease-in-out motion-reduce:transition-none group-data-[collapsible=icon]:px-1.5';
const WORDMARK_REVEAL_CLASS =
  'ml-2 max-w-40 overflow-hidden opacity-100 transition-[max-width,margin,opacity] duration-300 ease-in-out motion-reduce:transition-none group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0';

export function AppSidebar() {
  const { state } = useSidebar();
  const [searchText, setSearchText] = useState('');
  const [syncedState, setSyncedState] = useState(state);

  if (state !== syncedState) {
    setSyncedState(state);
    if (state === 'collapsed') setSearchText('');
  }

  const navVisibility = useNavVisibilityContext();
  const sections = filterNavSections(NAV_SECTIONS, searchText, navVisibility);
  const isSearching = searchText.trim() !== '';

  return (
    <Sidebar
      variant="sidebar"
      collapsible="icon"
      expandOnHover
      className="sidebar-scheme bg-sidebar *:data-[slot=sidebar-inner]:sidebar-surface"
    >
      <SidebarHeader className={HEADER_CLASS}>
        <Link to="/" className="flex min-w-0 items-center">
          <AppLogo className="size-8" />
          <span className={WORDMARK_REVEAL_CLASS}>
            <AppWordmark className="h-5" />
          </span>
        </Link>
        <SidebarToggleButton />
      </SidebarHeader>

      <SidebarContent className="space-y-3 px-(--sidebar-inset) pt-2 pb-4 group-data-[collapsible=icon]:px-2">
        <SidebarSearch value={searchText} onChange={setSearchText} />

        {sections.length === 0 && (
          <p className="px-2.5 py-6 text-center text-sm text-muted-foreground">
            No menu matches &ldquo;{searchText}&rdquo;
          </p>
        )}

        {sections.map((section, index) => (
          <Fragment key={section.id}>
            {index > 0 && (
              <SidebarSeparator
                className={cn(SECTION_SEPARATOR_CLASS, 'group-data-[collapsible=icon]:mx-0')}
              />
            )}
            <SidebarGroup className={SECTION_CLASS}>
              {section.label === undefined ? null : (
                <SidebarGroupLabel className={SECTION_LABEL_CLASS}>
                  {section.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className="gap-1">
                  {section.groups.map((group) => (
                    <CollapsibleNavGroup key={group.label} group={group} forceOpen={isSearching} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </Fragment>
        ))}
      </SidebarContent>

      <SidebarSeparator className={FOOTER_SEPARATOR_CLASS} />
      <SidebarFooter className={SIDEBAR_FOOTER_CLASS}>
        <VendorCredit layout="stacked" className={VENDOR_CREDIT_CLASS} />
      </SidebarFooter>
    </Sidebar>
  );
}
